import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import TitleAndBack from 'components/v2/TitleAndBack';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import EpdRoutes from 'routes/EpdRoutes';
import { toaster } from 'components/v2/toast';
import { ACCESS_DENIED_DETAIL } from 'util/epdActionErrorUtils';
import { getApiErrorStatus } from 'util/apiErrorUtils';
import { getDocumentDataSources, getDocument, getPortalSchema, getPropertiesSchema } from 'services/api/dynamicFormsApi';
import { Container } from 'styles/Styles.styled';
import { WizardHeaderGrid } from 'styles/v2/Styles.styled';
import DynamicContentRenderer from './DynamicContentRenderer';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import createDocumentStore from './store/createDocumentStore';
import { StoreContext } from './hooks/useDocumentStore';
import DataSyncIndicator from './components/DataSyncIndicator';
import { ConfirmDialog } from 'primereact/confirmdialog';

interface Params {
  docId: string;
}

const DynamicForm: React.FC<RouteComponentProps<Params>> = ({
  match: {
    params: { docId },
  },
  history,
}) => {
  const docQuery = useQuery({
    queryKey: ['DOC', docId],
    queryFn: () => getDocument(docId),
    // Let the access-denied (403) error fall through so we can redirect with a toast (as in SW/Compiler);
    // any other error is thrown to the error boundary.
    throwOnError: (error) => getApiErrorStatus(error) !== 403,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const propertiesSchemaQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'PROPERTIES', docQuery.data?.schemaId],
    queryFn: () => getPropertiesSchema(docQuery.data!.schemaId),
    enabled: !!docQuery.data,
    throwOnError: true,
  });
  const portalSchemaQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'PORTAL', docQuery.data?.schemaId],
    queryFn: () => getPortalSchema(docQuery.data!.schemaId),
    enabled: !!docQuery.data,
    throwOnError: true,
  });
  const dataSourcesQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'DATA_SOURCES', docQuery.data?.schemaId, docId],
    queryFn: () => getDocumentDataSources(docQuery.data!.schemaId, docId),
    enabled: !!docQuery.data,
    throwOnError: true,
  });

  const storeDeps = [docQuery.data, propertiesSchemaQuery.data, portalSchemaQuery.data, dataSourcesQuery.data];
  const store = useMemo(() => {
    const allLoaded = storeDeps.every(Boolean);
    if (!allLoaded) {
      return null;
    }

    return createDocumentStore({
      docId,
      initialData: docQuery.data!.data,
      initialSchemaId: docQuery.data!.schemaId,
      initialDataSources: dataSourcesQuery.data,
      state: docQuery.data!.state,
      metadata: docQuery.data!.metadata,
      properties: propertiesSchemaQuery.data,
    });
  }, storeDeps);

  if (docQuery.isError && getApiErrorStatus(docQuery.error) === 403) {
    toaster({
      severity: 'error',
      summary: 'Access denied',
      details: ACCESS_DENIED_DETAIL,
      // Stable id so a re-render / StrictMode double-invoke does not stack duplicate toasts.
      options: { toastId: 'dynamic-forms-access-denied' },
    });
    return <Redirect to={EpdRoutes.dashboard} />;
  }

  if (!store) {
    return (
      <Container>
        <WizardHeaderGrid>
          <TitleAndBack title={'Loading...'} backTo={'Back to the dashboard'} onBack={() => history.push(EpdRoutes.dashboard)} />
        </WizardHeaderGrid>
      </Container>
    );
  }

  return (
    <StoreContext.Provider value={store}>
      <DataSyncIndicator />
      <DynamicContentRenderer schema={portalSchemaQuery.data!} />

      {/* Dummy dialog so that 'confirmDialog' function can be called somewhere inside the tree */}
      <ConfirmDialog />
    </StoreContext.Provider>
  );
};

export default withGreyBackground(DynamicForm);
